import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Check, X, Pencil, Users, Ship, Car, Utensils, BusFront, Bell, History, UsersRound, UserPlus, Trash2, ShieldCheck, BookOpen, Sparkles, ClipboardList, KeyRound, ShoppingBag, ChefHat, LayoutDashboard, Monitor, Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Field, TextInput, SelectInput } from "@/components/site/Field";
import Calendar from "@/components/admin/Calendar";
import MenuEditor from "@/components/admin/MenuEditor";
import ContentEditor from "@/components/admin/ContentEditor";
import TakeawayEditor from "@/components/admin/TakeawayEditor";
import KitchenTickets from "@/components/admin/KitchenTickets";
import AdminHome from "@/components/admin/AdminHome";
import { IMAGES } from "@/lib/data";

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const STATUS_STYLE = {
  pending: "bg-primary text-foreground",
  confirmed: "bg-secondary text-secondary-foreground",
  cancelled: "bg-muted text-muted-foreground line-through",
};

function ChangeModal({ kind, booking, onClose, onSaved }) {
  const [date, setDate] = useState(booking.date);
  const [time, setTime] = useState(booking.time);
  const [times, setTimes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/ferry/times?date=${date}`).then(({ data }) => {
      setTimes(data.times);
      if (!data.times.includes(time)) setTime(data.times[0] || "");
    }).catch(() => {});
    // eslint-disable-next-line
  }, [date]);

  const save = async (status) => {
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${kind}/${booking.id}`, { status, date, time });
      toast.success(status === "confirmed" ? "Booking updated & confirmed" : "Booking updated");
      onSaved();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="change-modal">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-head font-extrabold text-2xl">Change date &amp; time</h3>
          <button onClick={onClose} className="p-1.5 border-2 border-foreground rounded-md" data-testid="change-close"><X size={16} /></button>
        </div>
        <p className="font-mono-plex text-sm text-foreground/60 mb-5">{booking.name} · {booking.party_size} {kind === "van" ? "for van" : "guests"} · {booking.phone}</p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date"><TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="change-date" /></Field>
            <Field label="Ferry / time">
              <SelectInput value={time} onChange={(e) => setTime(e.target.value)} data-testid="change-time">
                {times.map((t) => <option key={t} value={t}>{t}</option>)}
              </SelectInput>
            </Field>
          </div>
          <p className="font-mono-plex text-[11px] text-foreground/50">Only the date and time can be changed. The customer's original request is kept on record below.</p>

          {booking.history?.length > 0 && (
            <div className="border-2 border-foreground/20 rounded-lg p-3 bg-muted/50">
              <p className="overline text-foreground/60 mb-2 flex items-center gap-1.5"><History size={12} /> Change record</p>
              <ul className="space-y-1 font-mono-plex text-[11px] text-foreground/70">
                {booking.history.map((h) => (
                  <li key={h.changed_at}>{h.from_date} {h.from_time} → {h.to_date} {h.to_time} <span className="text-foreground/40">· {h.changed_by}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-7">
          <button onClick={() => save(booking.status === "confirmed" ? "confirmed" : "pending")} disabled={saving} data-testid="change-save"
            className="flex-1 font-head font-bold border-2 border-foreground rounded-full py-3 hover:bg-muted transition-colors">Save changes</button>
          <button onClick={() => save("confirmed")} disabled={saving} data-testid="change-confirm"
            className="flex-1 font-head font-bold bg-secondary text-secondary-foreground border-2 border-foreground rounded-full py-3 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all">Save &amp; confirm</button>
        </div>
      </div>
    </div>
  );
}

function BookingCard({ kind, b, onAct, onChange, canEdit }) {
  return (
    <div className="border-2 border-foreground rounded-xl p-4 bg-background" data-testid={`booking-${b.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-head font-bold text-lg">{b.name}</p>
          <p className="font-mono-plex text-xs text-foreground/60">{b.date} · {b.time}</p>
        </div>
        <span className={`font-mono-plex text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLE[b.status]}`} data-testid={`status-${b.id}`}>{b.status}</span>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono-plex text-xs text-foreground/70">
        <span className="flex items-center gap-1"><Users size={13} /> {b.party_size} {kind === "van" ? "for van" : "guest(s)"}</span>
        {kind === "table" && b.arrival && <span className="flex items-center gap-1">{b.arrival === "ferry" ? <><Ship size={13} /> By ferry</> : <><Car size={13} /> On-site</>}</span>}
        <span>{b.phone}</span>
        <span>{b.email}</span>
      </div>
      {b.notes && <p className="mt-2 text-sm text-foreground/60 italic">“{b.notes}”</p>}

      {b.history?.length > 0 && (
        <p className="mt-2 font-mono-plex text-[11px] text-secondary flex items-center gap-1.5" data-testid={`history-${b.id}`}>
          <History size={12} /> Rescheduled from {b.history[0].from_date} {b.history[0].from_time}
        </p>
      )}

      {canEdit && b.status !== "cancelled" && (
        <div className="mt-4 flex flex-wrap gap-2">
          {b.status !== "confirmed" && (
            <button onClick={() => onAct(b, "confirmed")} data-testid={`confirm-${b.id}`}
              className="flex items-center gap-1.5 font-head font-bold text-sm bg-secondary text-secondary-foreground border-2 border-foreground rounded-full px-4 py-2 hover:opacity-90"><Check size={14} /> Confirm</button>
          )}
          <button onClick={() => onChange(b)} data-testid={`change-${b.id}`}
            className="flex items-center gap-1.5 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-muted"><Pencil size={14} /> Change</button>
          <button onClick={() => onAct(b, "cancelled")} data-testid={`cancel-${b.id}`}
            className="flex items-center gap-1.5 font-head font-bold text-sm border-2 border-destructive text-destructive rounded-full px-4 py-2 hover:bg-destructive hover:text-destructive-foreground transition-colors"><X size={14} /> Cancel</button>
        </div>
      )}
    </div>
  );
}

function PermPicker({ value, onChange, idPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-1.5 text-sm">
        <span className="font-mono-plex text-xs text-foreground/60">Bookings</span>
        <SelectInput value={value.bookings} data-testid={`${idPrefix}-bookings`}
          onChange={(e) => onChange({ ...value, bookings: e.target.value })}>
          <option value="full">Full</option>
          <option value="view">View only</option>
          <option value="none">No access</option>
        </SelectInput>
      </label>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
        <input type="checkbox" checked={value.menu} data-testid={`${idPrefix}-menu`}
          onChange={(e) => onChange({ ...value, menu: e.target.checked })} />
        <span className="font-mono-plex text-xs">Edit menu</span>
      </label>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
        <input type="checkbox" checked={value.content} data-testid={`${idPrefix}-content`}
          onChange={(e) => onChange({ ...value, content: e.target.checked })} />
        <span className="font-mono-plex text-xs">Edit specials/story</span>
      </label>
    </div>
  );
}

function TeamModal({ currentEmail, onClose }) {
  const [members, setMembers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [perm, setPerm] = useState({ bookings: "full", menu: false, content: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/managers"); setMembers(data); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/managers/invite", { name, email, invite_base: window.location.origin, permissions: perm });
      if (data.email_sent) toast.success(`Invite emailed to ${email}`);
      else toast.success(`Manager added. Email didn't send — share this link: ${data.invite_link}`, { duration: 10000 });
      setName(""); setEmail(""); setPerm({ bookings: "full", menu: false, content: false }); load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setBusy(false); }
  };

  const savePerms = async (m, permissions) => {
    try { await api.patch(`/admin/managers/${m.id}`, { permissions }); toast.success(`${m.name}'s access updated`); load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    try { await api.delete(`/admin/managers/${m.id}`); toast.success("Member removed"); load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg w-full max-w-2xl p-6 md:p-8 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="team-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-head font-extrabold text-2xl flex items-center gap-2"><UsersRound size={22} /> Team</h3>
          <button onClick={onClose} className="p-1.5 border-2 border-foreground rounded-md" data-testid="team-close"><X size={16} /></button>
        </div>
        <p className="font-mono-plex text-xs text-foreground/60 mb-5">Managers set their own password from the invite email. Choose what each person can do.</p>

        <form onSubmit={invite} className="border-2 border-foreground rounded-xl p-4 mb-6 space-y-3" data-testid="invite-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name"><TextInput required value={name} onChange={(e) => setName(e.target.value)} placeholder="Sam Rangi" data-testid="invite-name" /></Field>
            <Field label="Email"><TextInput required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sam@email.com" data-testid="invite-email" /></Field>
          </div>
          <PermPicker value={perm} onChange={setPerm} idPrefix="invite" />
          <button type="submit" disabled={busy} data-testid="invite-submit"
            className="w-full font-head font-bold bg-foreground text-background rounded-full px-5 py-3 flex items-center justify-center gap-2 hover:bg-primary hover:text-foreground transition-colors disabled:opacity-60">
            <UserPlus size={16} /> Send invite
          </button>
        </form>

        <div className="space-y-3" data-testid="members-list">
          {members.map((m) => (
            <div key={m.id} className="border-2 border-foreground/20 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-head font-bold flex items-center gap-2">{m.name}
                    {m.is_owner && <span className="inline-flex items-center gap-1 font-mono-plex text-[10px] uppercase bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full"><ShieldCheck size={11} /> Owner</span>}
                  </p>
                  <p className="font-mono-plex text-xs text-foreground/60 truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`font-mono-plex text-[10px] uppercase px-2 py-0.5 rounded-full ${m.status === "active" ? "bg-secondary/20 text-secondary" : "bg-primary text-foreground"}`}>{m.status === "active" ? "Active" : "Invited"}</span>
                  {!m.is_owner && m.email !== currentEmail && (
                    <button onClick={() => remove(m)} data-testid={`remove-${m.id}`} className="p-2 border-2 border-destructive text-destructive rounded-md hover:bg-destructive hover:text-destructive-foreground transition-colors"><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
              {m.is_owner ? (
                <p className="mt-2 font-mono-plex text-[11px] text-foreground/50">Full access to everything.</p>
              ) : (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <PermPicker value={{ bookings: m.permissions?.bookings || "full", menu: !!m.permissions?.menu, content: !!m.permissions?.content }}
                    onChange={(p) => setMembers((arr) => arr.map((x) => (x.id === m.id ? { ...x, permissions: p } : x)))} idPrefix={`m-${m.id}`} />
                  <button onClick={() => savePerms(m, m.permissions)} data-testid={`save-perms-${m.id}`}
                    className="font-head font-bold text-xs border-2 border-foreground rounded-full px-3 py-1.5 hover:bg-muted">Save access</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (next !== confirm) return toast.error("New passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      onClose();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg w-full max-w-md p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="password-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-head font-extrabold text-2xl flex items-center gap-2"><KeyRound size={20} /> Change password</h3>
          <button onClick={onClose} className="p-1.5 border-2 border-foreground rounded-md" data-testid="password-close"><X size={16} /></button>
        </div>
        <p className="font-mono-plex text-xs text-foreground/60 mb-5">Choose a new password for your own account. Minimum 8 characters.</p>
        <form onSubmit={save} className="space-y-4" data-testid="password-form">
          <Field label="Current password"><TextInput type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pw-current" autoComplete="current-password" /></Field>
          <Field label="New password"><TextInput type="password" required value={next} onChange={(e) => setNext(e.target.value)} data-testid="pw-new" autoComplete="new-password" /></Field>
          <Field label="Confirm new password"><TextInput type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm" autoComplete="new-password" /></Field>
          <button type="submit" disabled={busy} data-testid="pw-submit"
            className="w-full font-head font-bold bg-secondary text-secondary-foreground border-2 border-foreground rounded-full py-3 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
            {busy ? "Saving…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("table");
  const [section, setSection] = useState("dashboard");
  const [tableB, setTableB] = useState([]);
  const [vanB, setVanB] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editing, setEditing] = useState(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem("hh_admin_mode");
    if (saved === "touch" || saved === "desktop") return saved;
    const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    return coarse || window.innerWidth < 1024 ? "touch" : "desktop";
  });
  const touch = mode === "touch";
  const toggleMode = () => setMode((m) => {
    const n = m === "touch" ? "desktop" : "touch";
    localStorage.setItem("hh_admin_mode", n);
    return n;
  });

  useEffect(() => { if (ready && !user) nav("/admin/login"); }, [ready, user, nav]);

  const fetchAll = useCallback(async () => {
    try {
      const [t, v] = await Promise.all([api.get("/admin/bookings/table"), api.get("/admin/bookings/van")]);
      setTableB(t.data); setVanB(v.data);
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }, []);

  useEffect(() => {
    const canView = user && (user.is_owner || user.permissions?.bookings === "full" || user.permissions?.bookings === "view");
    if (canView) fetchAll();
  }, [user, fetchAll]);

  if (!ready || !user) return <div className="min-h-screen bg-muted" />;

  const perms = user.permissions || {};
  const canBookings = user.is_owner || perms.bookings === "full";
  const canViewBookings = user.is_owner || perms.bookings === "full" || perms.bookings === "view";
  const canMenu = user.is_owner || !!perms.menu;
  const canContent = user.is_owner || !!perms.content;
  const activeSection = (section === "bookings" && !canViewBookings)
    ? (canMenu ? "menu" : canContent ? "content" : "bookings")
    : section;

  const bookings = tab === "table" ? tableB : vanB;
  const pending = bookings.filter((b) => b.status === "pending");
  const dayList = selectedDate ? bookings.filter((b) => b.date === selectedDate) : [];

  const act = async (b, status) => {
    try {
      await api.patch(`/admin/bookings/${tab}/${b.id}`, { status });
      toast.success(status === "confirmed" ? "Booking confirmed" : "Booking cancelled");
      fetchAll();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const navItems = [
    { id: "dashboard", icon: LayoutDashboard, label: "Dashboard", short: "Home", show: true },
    { id: "bookings", icon: ClipboardList, label: "Bookings", short: "Bookings", show: canViewBookings },
    { id: "kitchen", icon: ChefHat, label: "Kitchen", short: "Kitchen", show: canBookings },
    { id: "menu", icon: BookOpen, label: "Menu", short: "Menu", show: canMenu },
    { id: "takeaway", icon: ShoppingBag, label: "Takeaway", short: "Takeaway", show: canMenu },
    { id: "content", icon: Sparkles, label: "Specials & Story", short: "Content", show: canContent },
  ].filter((i) => i.show);

  return (
    <div className={`min-h-screen bg-background ${touch ? "touch-mode pb-24" : ""}`}>
      <header className="sticky top-0 z-30 bg-background border-b-2 border-foreground">
        <div className={`${touch ? "px-4" : "max-w-7xl mx-auto px-5 md:px-8"} h-16 flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-3 min-w-0">
            <img src={IMAGES.logo} alt="" className="h-9 w-9 object-contain shrink-0" />
            <span className="font-head font-extrabold tracking-tight truncate">Harbour Haze · Admin</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden lg:inline font-mono-plex text-xs text-foreground/50">{user.name}</span>
            <button onClick={toggleMode} data-testid="view-mode-toggle" title={touch ? "Touch layout — tap for Desktop" : "Desktop layout — tap for Touch"}
              className="flex items-center gap-2 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-muted">
              {touch ? <Smartphone size={15} /> : <Monitor size={15} />} <span className="hidden sm:inline">{touch ? "Touch" : "Desktop"}</span>
            </button>
            {user.is_owner && (
              <button onClick={() => setTeamOpen(true)} data-testid="open-team"
                className="flex items-center gap-2 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-muted"><UsersRound size={15} /> <span className="hidden sm:inline">Team</span></button>
            )}
            <button onClick={() => setPwOpen(true)} data-testid="open-password"
              className="flex items-center gap-2 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-muted"><KeyRound size={15} /> <span className="hidden sm:inline">Password</span></button>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout"
              className="flex items-center gap-2 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-muted"><LogOut size={15} /> <span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
      </header>

      <div className={touch ? "" : "max-w-7xl mx-auto flex items-start"}>
        {!touch && (
          <aside className="w-60 shrink-0 sticky top-16 self-start h-[calc(100vh-4rem)] overflow-y-auto border-r-2 border-foreground/10 py-6 px-3 flex flex-col gap-1.5" data-testid="admin-sidebar">
            {navItems.map(({ id, icon: Icon, label }) => (
              <button key={id} onClick={() => setSection(id)} data-testid={`section-${id}`}
                className={`w-full flex items-center gap-3 font-head font-bold text-sm px-4 py-3 rounded-xl border-2 transition-colors ${activeSection === id ? "bg-secondary text-secondary-foreground border-foreground" : "border-transparent hover:bg-muted"}`}>
                <Icon size={18} /> {label}
              </button>
            ))}
          </aside>
        )}
        <main className={touch ? "max-w-3xl mx-auto px-4 py-6" : "flex-1 min-w-0 px-6 lg:px-8 py-8"}>

        {activeSection === "dashboard" && (
          <AdminHome
            tableB={tableB} vanB={vanB}
            canViewBookings={canViewBookings} canBookings={canBookings}
            onOpenBooking={(kind) => { setTab(kind); setSelectedDate(todayStr()); setSection("bookings"); }}
          />
        )}
        {activeSection === "menu" && canMenu && <MenuEditor />}
        {activeSection === "takeaway" && canMenu && <TakeawayEditor />}
        {activeSection === "kitchen" && canBookings && <KitchenTickets />}
        {activeSection === "content" && canContent && <ContentEditor />}

        {activeSection === "bookings" && canViewBookings && (
        <>
        <div className="flex gap-2 mb-8">
          <button onClick={() => { setTab("table"); setSelectedDate(null); }} data-testid="tab-table"
            className={`flex items-center gap-2 font-head font-bold px-5 py-2.5 rounded-full border-2 border-foreground transition-colors ${tab === "table" ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            <Utensils size={16} /> Table bookings <span className="font-mono-plex text-xs">({tableB.filter(b=>b.status!=="cancelled").length})</span>
          </button>
          <button onClick={() => { setTab("van"); setSelectedDate(null); }} data-testid="tab-van"
            className={`flex items-center gap-2 font-head font-bold px-5 py-2.5 rounded-full border-2 border-foreground transition-colors ${tab === "van" ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            <BusFront size={16} /> Van bookings <span className="font-mono-plex text-xs">({vanB.filter(b=>b.status!=="cancelled").length})</span>
          </button>
        </div>

        {!canBookings && (
          <p className="mb-6 font-mono-plex text-xs bg-primary/20 border-2 border-foreground rounded-lg px-4 py-3" data-testid="view-only-note">
            You have view-only access to bookings — you can see everything but can't confirm, change or cancel.
          </p>
        )}

        <div className={touch ? "space-y-8" : "grid lg:grid-cols-12 gap-8 items-start"}>
          <div className={touch ? "space-y-4" : "lg:col-span-5 space-y-4"}>
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-secondary" />
              <h2 className="font-head font-extrabold text-xl">Tasks to approve</h2>
              <span className="font-mono-plex text-sm bg-primary rounded-full px-2.5 py-0.5">{pending.length}</span>
            </div>
            {pending.length === 0 ? (
              <p className="font-mono-plex text-sm text-foreground/50 border-2 border-dashed border-foreground/30 rounded-xl p-6 text-center" data-testid="no-tasks">
                No pending {tab} bookings. All caught up!
              </p>
            ) : (
              pending.map((b) => <BookingCard key={b.id} kind={tab} b={b} onAct={act} onChange={setEditing} canEdit={canBookings} />)
            )}
          </div>

          <div className={touch ? "space-y-6" : "lg:col-span-7 space-y-6"}>
            <div className="flex items-center gap-2">
              <Ship size={18} className="text-secondary" />
              <h2 className="font-head font-extrabold text-xl">{tab === "table" ? "Table" : "Van"} calendar</h2>
              <span className="font-mono-plex text-xs text-foreground/50">confirmed bookings shown</span>
            </div>
            <Calendar bookings={bookings} selectedDate={selectedDate} onSelectDate={setSelectedDate} />

            {selectedDate && (
              <div className="space-y-3" data-testid="day-detail">
                <h3 className="font-head font-bold text-lg">Bookings on {selectedDate}</h3>
                {dayList.length === 0
                  ? <p className="font-mono-plex text-sm text-foreground/50">Nothing booked this day.</p>
                  : dayList.map((b) => <BookingCard key={b.id} kind={tab} b={b} onAct={act} onChange={setEditing} canEdit={canBookings} />)}
              </div>
            )}
          </div>
        </div>
        </>
        )}
        </main>
      </div>

      {touch && (
        <nav className="fixed bottom-0 inset-x-0 z-30 bg-background border-t-2 border-foreground flex overflow-x-auto no-scrollbar" data-testid="mobile-tabbar">
          {navItems.map(({ id, icon: Icon, short }) => (
            <button key={id} onClick={() => setSection(id)} data-testid={`section-${id}`}
              className={`relative flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2.5 transition-colors ${activeSection === id ? "text-secondary" : "text-foreground/55"}`}>
              {activeSection === id && <span className="absolute top-0 inset-x-3 h-0.5 bg-secondary rounded-full" />}
              <Icon size={22} className="shrink-0" />
              <span className="font-head font-bold text-[10px] leading-none whitespace-nowrap">{short}</span>
            </button>
          ))}
        </nav>
      )}

      {editing && (
        <ChangeModal kind={tab} booking={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }} />
      )}
      {teamOpen && <TeamModal currentEmail={user.email} onClose={() => setTeamOpen(false)} />}
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}
